/* The dials that decide how the point charts' marks look, separate from what they encode.
 *
 * Every default reproduces the chart as it shipped, so a host that passes nothing sees no change.
 * The presets are concepts to compare in Storybook, not settings a reader chooses. */

export type MarkShadingLook = {
  /** How dark the turned-away side may get, as a fraction of the lit side. Lower is rounder. */
  ambient: number;
  /** How much of the lit sphere is laid over the fill, 0 to 1. */
  strength: number;
  /** Multiplier on the rig's specular terms. 0 is matte. */
  gloss: number;
  /** Tightness of the highlight. Higher is a smaller, sharper glint. */
  shininess: number;
  /** Marks narrower than this, in CSS pixels, are left flat. */
  minimumDiameter: number;
};

export type MarkShadowLook = {
  /** Darkness of a shadow under a mark sitting on the floor. 0 draws none. */
  opacity: number;
  /** 0 is a hard-edged ellipse, 1 fades from the centre. */
  softness: number;
  /** How much a mark at the top of the box spreads and fades its shadow. 0 keeps every shadow alike. */
  heightSpread: number;
  light: ShadowLight;
  /**
   * Where the light comes from across the floor. `camera` moves with the viewer, so shadows slide across the
   * floor as it turns and always fall the same way on screen. `world` is a fixed sun, so shadows
   * turn with the floor.
   */
  lightSpace: 'camera' | 'world';
};

/**
 * How high the shadow light stands.
 *
 * `leaned` blends from straight overhead (`lean` 0, so a shadow marks the mark's x and y exactly)
 * toward the light chosen by `lightSpace` (1). `fixed` keeps that light's direction across the
 * floor at `elevation` radians above it, and is the only kind that can reach the back walls: they
 * face the camera, so only a low light from the front casts onto them.
 */
export type ShadowLight =
  | { kind: 'leaned'; lean: number }
  | { kind: 'fixed'; elevation: number; onWalls: boolean };

export type WallProjectionLook = {
  /** Strength of each mark's flattened copy on the two back walls. 0 draws none. */
  opacity: number;
  /** 0 is a grey shadow, 1 is the mark's own color. */
  tint: number;
  softness: number;
};

/**
 * A soft shadow each mark casts onto the marks just behind it, where they overlap on screen.
 *
 * Ambient occlusion in the one form a back-to-front 2D pass can afford: a dark ring just outside
 * the mark, laid down before it is drawn, so it only falls on what is already there. Clipped to the
 * marks behind and weighted by how close they are in depth, so a mark far behind that merely
 * overlaps on screen is not shaded as though the two touched.
 */
export type MarkContactLook = {
  /** Darkness at the mark's edge. 0 draws none. */
  opacity: number;
  /** How far the shadow reaches past the edge, as a fraction of the mark's radius. */
  spread: number;
  /** How far behind a mark can be and still be shaded, in the camera's cube units. */
  depthRange: number;
  /** Whether the empty background around a mark darkens too, not only the marks behind it. */
  halo: boolean;
};

export type MarkOutlineKind = 'surface' | 'deepenedHue' | 'none';

export type MarkOutlineLook = {
  /**
   * `surface` rings the mark in the background color, outside its edge. `deepenedHue` draws a
   * darker rim of the mark's own color inside its edge, so it adds no area. `none` draws neither.
   */
  kind: MarkOutlineKind;
  width: number;
  opacity: number;
};

export type PointsLook = {
  shading: MarkShadingLook;
  shadow: MarkShadowLook;
  outline: MarkOutlineLook;
  wallProjection: WallProjectionLook;
  contact: MarkContactLook;
  /** How far the farthest mark is washed toward the background, 0 to 1. */
  depthFade: number;
};

export type PointsLookOverrides = {
  [Key in keyof PointsLook]?: PointsLook[Key] extends object
    ? Partial<PointsLook[Key]>
    : PointsLook[Key];
};

export const DEFAULT_POINTS_LOOK: PointsLook = {
  shading: {
    ambient: 0.3,
    strength: 0.55,
    gloss: 1,
    shininess: 28,
    minimumDiameter: 10,
  },
  shadow: {
    opacity: 0,
    softness: 0.7,
    heightSpread: 1,
    light: { kind: 'leaned', lean: 0 },
    lightSpace: 'camera',
  },
  outline: { kind: 'surface', width: 2, opacity: 1 },
  wallProjection: { opacity: 0, tint: 0, softness: 0.6 },
  contact: { opacity: 0, spread: 0.45, depthRange: 0.3, halo: false },
  depthFade: 0,
};

export const resolvePointsLook = (
  overrides: PointsLookOverrides | undefined
): PointsLook => ({
  shading: { ...DEFAULT_POINTS_LOOK.shading, ...overrides?.shading },
  shadow: { ...DEFAULT_POINTS_LOOK.shadow, ...overrides?.shadow },
  outline: { ...DEFAULT_POINTS_LOOK.outline, ...overrides?.outline },
  wallProjection: {
    ...DEFAULT_POINTS_LOOK.wallProjection,
    ...overrides?.wallProjection,
  },
  contact: { ...DEFAULT_POINTS_LOOK.contact, ...overrides?.contact },
  depthFade: overrides?.depthFade ?? DEFAULT_POINTS_LOOK.depthFade,
});

export const POINTS_LOOK_CONCEPTS = {
  shipped: DEFAULT_POINTS_LOOK,
  grounded: resolvePointsLook({
    shadow: { opacity: 0.28, softness: 0.8, heightSpread: 1 },
    outline: { kind: 'surface', width: 1.25, opacity: 0.7 },
  }),
  glossy: resolvePointsLook({
    shading: { ambient: 0.22, strength: 0.7, gloss: 3, shininess: 60 },
    shadow: { opacity: 0.22, softness: 0.85, heightSpread: 1 },
    outline: { kind: 'deepenedHue', width: 1, opacity: 0.6 },
  }),
  softStudio: resolvePointsLook({
    shading: { ambient: 0.4, strength: 0.6, gloss: 0.5, minimumDiameter: 6 },
    shadow: { opacity: 0.18, softness: 1, heightSpread: 1.5 },
    outline: { kind: 'none' },
    depthFade: 0.25,
  }),
  sunlit: resolvePointsLook({
    shading: { ambient: 0.25, strength: 0.65, gloss: 2, shininess: 50 },
    shadow: {
      opacity: 0.3,
      softness: 0.75,
      heightSpread: 0.8,
      light: { kind: 'leaned', lean: 0.7 },
    },
    outline: { kind: 'deepenedHue', width: 1, opacity: 0.6 },
  }),
  castOnWalls: resolvePointsLook({
    shading: { ambient: 0.25, strength: 0.65, gloss: 2, shininess: 50 },
    shadow: {
      opacity: 0.3,
      softness: 0.75,
      heightSpread: 0.8,
      light: { kind: 'fixed', elevation: 0.35, onWalls: true },
    },
    outline: { kind: 'deepenedHue', width: 1, opacity: 0.6 },
  }),
  projected: resolvePointsLook({
    shading: { ambient: 0.25, strength: 0.65, gloss: 2, shininess: 50 },
    shadow: { opacity: 0.25, softness: 0.8, heightSpread: 1 },
    outline: { kind: 'deepenedHue', width: 1, opacity: 0.6 },
    wallProjection: { opacity: 0.35, tint: 0.6, softness: 0.5 },
  }),
  inked: resolvePointsLook({
    shading: { strength: 0.35, gloss: 0.5 },
    outline: { kind: 'deepenedHue', width: 1.5, opacity: 0.9 },
  }),
  molecular: resolvePointsLook({
    shading: { ambient: 0.4, strength: 0.5, gloss: 0.6 },
    contact: { opacity: 0.85, spread: 0.6, depthRange: 0.3, halo: false },
    outline: { kind: 'deepenedHue', width: 1, opacity: 0.55 },
  }),
} satisfies Record<string, PointsLook>;

export type PointsLookConcept = keyof typeof POINTS_LOOK_CONCEPTS;
